import type { IdsReport } from '@manudev/model';
import * as THREE from 'three';
import type { HeroData } from '@/hero/data';
import { MODELS_BASE } from '@/hero/preload';
import { elementBox } from '@/render/batch';
import type { ElementState, LayerRig } from '@/render/rig';
import { useSelectionStore } from '@/state/selection';
import { hero } from '@/state/tour';
import { STORE_LAYER } from '@/hero/StoreLayer';
import { useHeroStore } from '@/store/store';
import { layersOf, resetLayers, type Frame, type StationImpl } from '../types';
import { RulesCard } from './Card';
import { RulesPanel } from './Panel';
import { runKey, useRulesStore } from './state';
import { verdicts } from './verdicts';

interface Painted {
  report: IdsReport | null;
  data: HeroData | null;
  selected: number | null;
  built: number;
}

let painted: Painted | null = null;
let idsOf: { data: HeroData | null; built: unknown; map: Map<string, number> } = {
  data: null,
  built: null,
  map: new Map(),
};

export const rules: StationImpl = {
  pick: 'all',
  enter({ layers, height }) {
    resetLayers(layers, height);
    for (const h of layersOf(layers, (d) => d === 'mep')) h.rig.setVisible(false);
    for (const h of layersOf(layers, (d) => d === 'arch' || d === 'structure'))
      h.rig.setGhost(0.85);
    painted = null;
    if (hero.ifc) useHeroStore.getState().load(MODELS_BASE + hero.ifc);
  },
  update(frame) {
    const store = useHeroStore.getState();
    const state = useRulesStore.getState();
    if (store.status === 'ready' && state.ranFor !== runKey(state.active, store.version))
      void state.run();
    const built = frame.layers.get(STORE_LAYER)?.rig;
    // the built layer mounts after enter, so its ghost is set here
    built?.setGhost(0.85);
    const selected = useSelectionStore.getState().selected?.expressID ?? null;
    const next: Painted = {
      report: state.report,
      data: frame.data,
      selected,
      built: built?.elements.size ?? 0,
    };
    if (
      !painted ||
      painted.report !== next.report ||
      painted.data !== next.data ||
      painted.selected !== next.selected ||
      painted.built !== next.built
    ) {
      painted = next;
      paint(frame, next);
    }
    if (state.focus) {
      focusOn(frame, state.focus);
      state.requestFocus(null);
    }
  },
  leave({ layers, height }) {
    resetLayers(layers, height);
    useSelectionStore.getState().setFocus(null);
    painted = null;
  },
  Panel: RulesPanel,
  Card: RulesCard,
};

// every rig the check can colour: the hero's layers and what the visitor built
function rigs({ layers }: Frame): { id: string; rig: LayerRig; space: boolean }[] {
  return [...layers.values()].map((h) => ({
    id: h.model.id,
    rig: h.rig,
    space: h.model.discipline === 'space',
  }));
}

// GlobalId to expressID through the props data, with the store's snapshot for what was built
function expressIds(data: HeroData | null): Map<string, number> {
  const built = useHeroStore.getState().elements;
  if (idsOf.data === data && idsOf.built === built) return idsOf.map;
  const map = new Map<string, number>();
  for (const el of built) if (el.globalId) map.set(el.globalId, el.expressID);
  for (const el of data?.byId.values() ?? []) if (el.globalId) map.set(el.globalId, el.expressID);
  idsOf = { data, built, map };
  return map;
}

function paint(frame: Frame, { report, data, selected }: Painted) {
  const byExpressId = new Map<number, 'pass' | 'fail'>();
  if (report) {
    const ids = expressIds(data);
    for (const [globalId, verdict] of verdicts(report)) {
      const id = ids.get(globalId);
      if (id != null) byExpressId.set(id, verdict);
    }
  }
  for (const { rig, space } of rigs(frame)) {
    let touched = false;
    for (const el of rig.elements.values()) {
      const verdict = byExpressId.get(el.expressID);
      if (verdict) touched = true;
      const state: ElementState | null =
        el.expressID === selected
          ? 'highlight'
          : verdict === 'fail'
            ? space
              ? 'failSoft'
              : 'fail'
            : verdict === 'pass'
              ? 'pass'
              : null;
      rig.setElementState(el.expressID, state);
    }
    // rooms only show when the rule set says something about them
    if (space) rig.setVisible(touched);
  }
}

function focusOn(frame: Frame, globalId: string) {
  const expressID = expressIds(frame.data).get(globalId);
  if (expressID == null) return;
  const store = useSelectionStore.getState();
  for (const { id, rig } of rigs(frame)) {
    const el = rig.elements.get(expressID);
    if (!el) continue;
    const box = elementBox(el, new THREE.Box3());
    if (!box.isEmpty()) {
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      store.setFocus({
        center: sphere.center.toArray() as [number, number, number],
        radius: Math.max(sphere.radius, 1),
      });
    }
    store.select({ layer: id, expressID });
    return;
  }
}
